import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface AutocompleteSearchOption<T = unknown> {
  /** Unique, defined value used as both the form-control value and the @for track key. */
  value: T;
  label: string;
  sublabel?: string;
  dotColor?: string;
}

/**
 * Visual style for the trigger.
 * - `chip` (default): selected values render as chips with a dashed "+ Add" affordance.
 * - `filter`: a single compact button styled like a filter pill — `Label: value ▾`.
 */
export type AutocompleteSearchVariant = 'chip' | 'filter';

@Component({
  selector: 'ptl-autocomplete-search',
  templateUrl: './sg-autocomplete-search.component.html',
  styleUrls: ['./sg-autocomplete-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgAutocompleteSearchComponent),
      multi: true,
    },
  ],
})
export class SgAutocompleteSearchComponent<T = unknown> implements ControlValueAccessor {
  @Input() options: AutocompleteSearchOption<T>[] = [];
  @Input() multiSelect = false;
  @Input() label = '';
  @Input() addLabel = '+ Add';
  @Input() removeLabel = 'Remove';
  @Input() searchPlaceholder = 'Search…';
  @Input() searchAriaLabel = '';
  @Input() doneLabel = 'Done';
  @Input() selectedCountSuffix = 'selected';
  @Input() counterSuffix = '';
  @Input() counterTemplate?: (count: number) => string;
  @Input() disabled = false;
  @Input() isLoading = false;
  @Input() variant: AutocompleteSearchVariant = 'chip';
  /** Text shown in the filter-variant trigger when no value is selected (e.g. "All kinds"). */
  @Input() filterPlaceholder = '';
  /** When set, shows a "Clear all" link in the multi-select footer. */
  @Input() clearAllLabel?: string;

  @Output() selectionChange = new EventEmitter<T | T[] | null>();
  @Output() opened = new EventEmitter<void>();
  @Output() searchTermChange = new EventEmitter<string>();

  isOpen = false;
  searchText = '';
  pendingSelection: T[] = [];
  private value: T | T[] | null = null;

  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
  ];

  private onChange: (v: T | T[] | null) => void = () => undefined;
  private onTouched = () => undefined;

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(v: T | T[] | null): void {
    this.value = v;
    if (this.isOpen) {
      this.pendingSelection = this.valueToArray(v);
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: T | T[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(d: boolean): void {
    this.disabled = d;
    this.cdr.markForCheck();
  }

  get selectedOptions(): AutocompleteSearchOption<T>[] {
    const arr = this.valueToArray(this.value);
    return arr.map((v) => this.options.find((o) => o.value === v)).filter((o): o is AutocompleteSearchOption<T> => !!o);
  }

  get filterDisplayValue(): string {
    const selected = this.selectedOptions;
    if (!selected.length) {
      return this.filterPlaceholder;
    }
    if (selected.length === 1) {
      return selected[0].label;
    }
    return `${selected.length} ${this.selectedCountSuffix}`;
  }

  get filteredOptions(): AutocompleteSearchOption<T>[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.options;
    return this.options.filter((o) => o.label.toLowerCase().includes(q) || (o.sublabel ?? '').toLowerCase().includes(q));
  }

  get counterLabel(): string {
    const count = this.filteredOptions.length;
    if (this.counterTemplate) return this.counterTemplate(count);
    return this.counterSuffix ? `${count} ${this.counterSuffix}` : `${count}`;
  }

  isSelected(v: T): boolean {
    return this.pendingSelection.includes(v);
  }

  trackByValue = (_: number, opt: AutocompleteSearchOption<T>) => opt.value;

  toggleOpen(): void {
    if (this.disabled) return;
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    this.pendingSelection = this.valueToArray(this.value);
    this.searchText = '';
    this.isOpen = true;
    this.opened.emit();
  }

  onSearchInput(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
    this.searchTermChange.emit(this.searchText);
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.onTouched();
  }

  toggleOption(opt: AutocompleteSearchOption<T>): void {
    if (this.multiSelect) {
      const idx = this.pendingSelection.indexOf(opt.value);
      this.pendingSelection = idx === -1 ? [...this.pendingSelection, opt.value] : this.pendingSelection.filter((_, i) => i !== idx);
      return;
    }
    this.pendingSelection = [opt.value];
    this.commit();
    this.close();
  }

  removeChip(value: T, event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    const base = this.isOpen ? this.pendingSelection : this.valueToArray(this.value);
    this.pendingSelection = base.filter((v) => v !== value);
    this.commit();
  }

  onDone(): void {
    this.commit();
    this.close();
  }

  onClearAll(): void {
    this.pendingSelection = [];
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.close();
  }

  private commit(): void {
    if (this.multiSelect) {
      const arr = [...this.pendingSelection];
      this.value = arr;
      this.onChange(arr);
      this.selectionChange.emit(arr);
      return;
    }
    const v = this.pendingSelection[0] ?? null;
    this.value = v;
    this.onChange(v);
    this.selectionChange.emit(v);
  }

  private valueToArray(v: T | T[] | null): T[] {
    if (v == null) return [];
    return Array.isArray(v) ? [...v] : [v];
  }
}
